// Contact Form Handler Web Component
class ContactFormHandler extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.submitButton = null;
    this.blockId = null;
    this.handleSubmitBound = this.handleSubmit.bind(this);
    this.initialized = false;
  }

  static get observedAttributes() {
    return ["form-id", "recaptcha-site-key", "email-post-url", "redirect-url"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // If form-id is set and we haven't initialized yet, try to initialize
    if (name === "form-id" && newValue && !this.initialized) {
      // Small delay to ensure form exists in DOM
      setTimeout(() => this.tryInitialize(), 50);
    }
  }

  connectedCallback() {
    // Try to initialize if form-id is already set
    if (this.getAttribute("form-id")) {
      setTimeout(() => this.tryInitialize(), 50);
    }
  }

  tryInitialize() {
    if (this.initialized) {
      return;
    }

    const formId = this.getAttribute("form-id");
    if (!formId) {
      return; // Wait for form-id to be set
    }

    // Get the block ID from form ID (format: contact_form_{blockId})
    const match = formId.match(/contact_form_(.+)/);
    if (match) {
      this.blockId = match[1];
    }

    // Find the form
    this.form = document.getElementById(formId);

    if (!this.form) {
      // Form not found yet, might be a timing issue - try once more
      setTimeout(() => {
        this.form = document.getElementById(formId);
        if (!this.form) {
          console.warn(
            "Contact form not found with ID:",
            formId,
            "- handler will not initialize"
          );
          return;
        }
        this.initializeForm();
      }, 200);
      return;
    }

    this.initializeForm();
  }

  initializeForm() {
    if (!this.form || this.initialized) {
      return;
    }

    // Get settings from attributes
    this.recaptchaSiteKey = this.getAttribute("recaptcha-site-key") || "";
    this.emailPostUrl = this.getAttribute("email-post-url") || "";
    this.redirectUrl = this.getAttribute("redirect-url") || "";

    this.submitButton = this.form.querySelector('button[type="submit"]');

    // Set up form submission handler
    this.form.addEventListener("submit", this.handleSubmitBound);

    // Only set up reCAPTCHA if key is provided
    if (this.recaptchaSiteKey) {
      // Set up reCAPTCHA callback for this specific form
      if (this.blockId) {
        const callbackName = "recaptcha_v2_callback_" + this.blockId;
        window[callbackName] = this.recaptchaCallback.bind(this);
      } else {
        window.recaptcha_v2_callback = this.recaptchaCallback.bind(this);
      }

      // Load reCAPTCHA script if not already loaded
      if (!window.grecaptcha) {
        this.loadRecaptcha();
      } else {
        // reCAPTCHA already loaded, render it for this form
        this.renderRecaptcha();
      }
    } else {
      // No reCAPTCHA, enable submit button immediately
      if (this.submitButton) {
        this.submitButton.disabled = false;
      }
    }

    this.initialized = true;
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener("submit", this.handleSubmitBound);
    }

    // Clean up callback
    if (this.blockId) {
      const callbackName = "recaptcha_v2_callback_" + this.blockId;
      delete window[callbackName];
    }
  }

  loadRecaptcha() {
    if (document.querySelector('script[src*="google.com/recaptcha"]')) {
      // Script already loading or loaded
      if (window.grecaptcha) {
        this.renderRecaptcha();
      } else {
        // Wait for it to load
        const checkInterval = setInterval(() => {
          if (window.grecaptcha) {
            clearInterval(checkInterval);
            this.renderRecaptcha();
          }
        }, 100);
        setTimeout(() => clearInterval(checkInterval), 10000); // Timeout after 10s
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          this.renderRecaptcha();
        });
      }
    };
    document.head.appendChild(script);
  }

  renderRecaptcha() {
    if (!window.grecaptcha || !this.recaptchaSiteKey) return;

    const recaptchaContainer = this.form.querySelector(".g-recaptcha");
    if (!recaptchaContainer) return;

    // Check if already rendered
    if (recaptchaContainer.querySelector("iframe")) return;

    const callbackName = this.blockId
      ? "recaptcha_v2_callback_" + this.blockId
      : "recaptcha_v2_callback";

    window.grecaptcha.ready(() => {
      try {
        const widgetId = window.grecaptcha.render(recaptchaContainer, {
          sitekey: this.recaptchaSiteKey,
          callback: callbackName,
        });
        // Store widget ID for later use
        if (widgetId !== undefined) {
          recaptchaContainer.dataset.recaptchaId = widgetId;
        }
      } catch (error) {
        console.error("reCAPTCHA render error:", error);
      }
    });
  }

  recaptchaCallback() {
    // Enable submit button when captcha is completed
    if (this.submitButton) {
      this.submitButton.disabled = false;
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (!this.form) return;

    // Check reCAPTCHA only if key is provided
    let recaptchaResponse = null;
    if (this.recaptchaSiteKey) {
      // Find the reCAPTCHA widget ID for this form
      let recaptchaWidgetId = null;
      const recaptchaContainer = this.form.querySelector(".g-recaptcha");
      if (recaptchaContainer && recaptchaContainer.dataset.recaptchaId) {
        recaptchaWidgetId = parseInt(
          recaptchaContainer.dataset.recaptchaId,
          10
        );
      }

      // Check if reCAPTCHA v2 has been completed
      if (window.grecaptcha) {
        try {
          if (recaptchaWidgetId !== null) {
            recaptchaResponse =
              window.grecaptcha.getResponse(recaptchaWidgetId);
          } else {
            // Fallback: try to get response without widget ID
            recaptchaResponse = window.grecaptcha.getResponse();
          }
        } catch (error) {
          console.error("reCAPTCHA error:", error);
        }
      }

      if (!recaptchaResponse) {
        const errorMsg =
          this.getTranslation("contact.section.captcha_required") ||
          "Please complete the CAPTCHA verification.";
        alert(errorMsg);
        return;
      }
    }

    // Set loading state on button
    if (this.submitButton) {
      if (
        this.submitButton.hasAttribute("is") &&
        this.submitButton.getAttribute("is") === "loader-button"
      ) {
        this.submitButton.setAttribute("aria-busy", "true");
      } else {
        this.submitButton.disabled = true;
        const originalText = this.submitButton.textContent;
        this.submitButton.textContent =
          this.getTranslation("contact.section.submitting") || "Submitting...";
        this.submitButton.dataset.originalText = originalText;
      }
    }

    // Check if POST URL is provided
    if (!this.emailPostUrl) {
      // No POST URL, form should use Shopify default handler
      // This shouldn't happen if form has data-use-custom-handler, but handle gracefully
      console.warn(
        "No email POST URL provided, form should use Shopify default handler"
      );
      this.resetButton();
      return;
    }

    try {
      // Prepare form data
      const formData = new FormData(this.form);

      // Build data object for backend
      const data = {
        firstname: formData.get("firstname"),
        lastname: formData.get("lastname"),
        email: formData.get("email"),
        message: formData.get("message"),
        phone: formData.get("phone") || "",
        company: formData.get("company") || "",
        sms_opt_in: formData.get("sms_opt_in") ? "true" : "false",
        hs_context: JSON.stringify({
          pageUri: window.location.href,
          pageName: document.title,
        }),
      };

      // Only include reCAPTCHA token if key is provided and response exists
      if (this.recaptchaSiteKey && recaptchaResponse) {
        data.recaptchaToken = recaptchaResponse;
      }

      // Submit to backend
      const response = await fetch(this.emailPostUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.success) {
        // Reset button state first
        this.resetButton();

        // Clear form fields
        this.form.reset();

        // Show success message
        let confirmationMsg = null;
        if (this.blockId) {
          confirmationMsg = document.getElementById(
            "confirmation-message_" + this.blockId
          );
        } else {
          // Fallback: try to find confirmation message near the form
          confirmationMsg = this.form.parentNode.querySelector(
            ".confirmation-message, #confirmation-message"
          );
        }
        if (confirmationMsg) {
          confirmationMsg.style.display = "block";
        }

        // Fire Google Ads conversion, then redirect if URL provided
        if (this.redirectUrl) {
          this.reportConversion(this.redirectUrl);
        }
      } else {
        const errorMsg =
          this.getTranslation("contact.section.submit_error") ||
          "There was an error submitting the form.";
        alert(errorMsg);
        this.resetButton();
      }
    } catch (error) {
      console.error("Form submission error:", error);
      const errorMsg =
        this.getTranslation("contact.section.form_error_generic") ||
        "There was an error submitting the form. Please try again.";
      alert(errorMsg);
      this.resetButton();
    }
  }

  resetButton() {
    if (this.submitButton) {
      if (
        this.submitButton.hasAttribute("is") &&
        this.submitButton.getAttribute("is") === "loader-button"
      ) {
        this.submitButton.setAttribute("aria-busy", "false");
      } else {
        this.submitButton.disabled = false;
        this.submitButton.textContent =
          this.submitButton.dataset.originalText ||
          this.getTranslation("contact.section.submit") ||
          "Submit";
      }
    }
  }

  reportConversion(url) {
    const redirectCallback = function () {
      if (typeof url !== "undefined" && url) {
        window.location = url;
      }
    };

    if (typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: "AW-621069237/z80pCOCEleUCELWHk6gC",
        event_callback: redirectCallback,
      });
    } else {
      // gtag not available, redirect immediately
      redirectCallback();
    }
  }

  getTranslation(key) {
    // Try to get from contact section translations (format: contact.section.key)
    const translationKey = key.replace("contact.section.", "");
    if (
      window.contactSectionTranslations &&
      window.contactSectionTranslations[translationKey]
    ) {
      return window.contactSectionTranslations[translationKey];
    }

    // Fallback: try to get from Shopify theme strings
    if (window.theme && window.theme.strings && window.theme.strings[key]) {
      return window.theme.strings[key];
    }

    // Fallback: try to get from Shopify.translations if available
    if (
      window.Shopify &&
      window.Shopify.translations &&
      window.Shopify.translations[key]
    ) {
      return window.Shopify.translations[key];
    }

    return null;
  }
}

// Register the web component
if (!customElements.get("contact-form-handler")) {
  customElements.define("contact-form-handler", ContactFormHandler);
}
